import React, { useMemo, useState } from 'react';
import './Lookbook.css';
import Modal from '../../common/Modal/modal';
import { ZoomIn, ChevronDown } from 'lucide-react';

function Lookbook({ lookbook, images: propImages, title }) {
  // State for modal visibility and selected image
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // State for expand/collapse functionality
  const [isExpanded, setIsExpanded] = useState(false);


  // Extract data from props (either lookbook object or individual props)
  const lookbookImages = propImages || lookbook?.images || [];
  const lookbookTitle = title || lookbook?.title || lookbook?.name;
  const lookbookDescription = lookbook?.description;

  // Bandwidth hint for low-data users (2G/save-data mode)
  // Used to optimize image loading behavior on constrained networks.
  const isLowBandwidth = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) {
      return false;
    }

    return connection.saveData === true || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
  }, []);

  // Handle image click - open modal with full resolution image
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  // Show loading state if images not yet provided
  if (!lookbookImages || lookbookImages.length === 0) {
    return (
      <div className="lookbook-container">
        <div className="lookbook-empty">
          Aucune image disponible pour ce lookbook.
        </div>
      </div>
    );
  }

  return (
    <div className="lookbook-container">
      {/* Lookbook title - clickable to expand/collapse */}
      {lookbookTitle && (
        <button
          type="button"
          className="lookbook-title-wrapper"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={`${lookbookTitle} - cliquez pour ${isExpanded ? 'réduire' : 'développer'}`}
        >
          <h3 className="lookbook-title">
            {lookbookTitle}
          </h3>
          <ChevronDown
            className={`lookbook-title-chevron ${isExpanded ? 'expanded' : 'pulse'}`}
            size={48}
          />
        </button>
      )}

      {/* Lookbook description if provided - only show when expanded */}
      {lookbookDescription && isExpanded && (
        <p className="lookbook-description">{lookbookDescription}</p>
      )}

      {/* Image grid - responsive layout - only show when expanded */}
      {isExpanded && (
      <div className="lookbook-images" id={`lookbook-images-${lookbook?.id || 'default'}`}>
        {lookbookImages.map((image, index) => (
          <button
            type="button"
            key={`${lookbook?.id || 'lookbook'}-${index}`}
            className="lookbook-image-item"
            onClick={() => handleImageClick(image)}
            aria-label={`Image ${index + 1} du lookbook`}
          >
            {/* Thumbnail image with lazy loading */}
            <img
              src={image}
              alt={`Lookbook ${index + 1}`}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              draggable={false}
              onContextMenu={(event) => event.preventDefault()}
              fetchPriority={isLowBandwidth ? 'low' : 'auto'}
            />

            {/* Hover overlay with magnifying glass icon */}
            <div className="lookbook-image-overlay">
              <ZoomIn strokeWidth={1.5} />
            </div>
          </button>
        ))}
      </div>
      )}

      {/* Modal for viewing full resolution image */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="lookbook-modal-image-container">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Lookbook en plein ecran"
              className="lookbook-modal-image"
              decoding="async"
              referrerPolicy="no-referrer"
              draggable={false}
              onContextMenu={(event) => event.preventDefault()}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Lookbook;
